import React, { useContext, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useNavigate } from 'react-router-dom';
import ReorderResizeTable from 'components/ReorderResizeTable';
import {
  CUSTOM_COL_SIZES,
  HEADING_TRANSLATIONS,
  SORTING_VALUES,
  TABLE_COLUMNS,
} from './components/data';
import { BODY_CONTENT_GENERATE } from './components';
import { DealRoom } from 'models/DealRoom';
import { getSellingPortfolio } from 'utils';
import useQueryParams from 'hooks/useQueryParams';
import usePageParams from 'hooks/usePageParams';
import api from 'api';
import EnterpriseContext from '../../../../providers/Enterprise/Enterprise.context';
import useNumberOfResultsPerPage from 'hooks/useNumberOfResultsPerPage';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';

import './AnalyticsDeals.styles.scss';

type AnalyticsDealsProps = {
  className?: string;
};

const AnalyticsDeals: React.FC<AnalyticsDealsProps> = (props) => {
  const { className } = props;

  const classes = classNames('analytics-deals', className);

  const [totalPages, setTotalPages] = useState(0);
  const [deals, setDeals] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const { enterprise } = useContext(EnterpriseContext);
  const { isOfficeManager, enterpriseMember } = useContext(CurrentUserContext);

  const page = usePageParams();
  const { params } = useQueryParams();
  const {
    stage,
    industry,
    colleagueIds,
    sort,
    timeframe,
    enterpriseBusinessUnitId: paramsEnterpriseBusinessUnitId,
  } = params;

  const enterpriseBusinessUnitId = useMemo(
    () =>
      isOfficeManager
        ? enterpriseMember?.enterpriseBusinessUnitId
        : paramsEnterpriseBusinessUnitId,
    [
      paramsEnterpriseBusinessUnitId,
      enterpriseMember?.enterpriseBusinessUnitId,
      isOfficeManager,
    ],
  );

  const { limit } = useNumberOfResultsPerPage();

  const renderedContent = useMemo(
    () =>
      TABLE_COLUMNS.reduce(
        (acc, col) => ({
          ...acc,
          [col]: (deal: DealRoom) => BODY_CONTENT_GENERATE[col]({ deal }),
        }),
        {},
      ),
    [],
  );

  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);

        const {
          data: { items, totalPages },
        } = await api.enterpriseAnalytics.getAllDeals(enterprise?._id, {
          $page: page,
          $limit: limit,
          $sort: sort,
          stage,
          industry,
          colleagueIds,
          enterpriseBusinessUnitId,
          timeframe,
        });

        setDeals(items);
        setTotalPages(totalPages);
      } catch (e) {
        return;
      } finally {
        setIsLoading(false);
      }
    })();
  }, [
    sort,
    colleagueIds,
    enterprise?._id,
    industry,
    page,
    stage,
    enterpriseBusinessUnitId,
    limit,
    timeframe,
  ]);

  return (
    <ReorderResizeTable
      className={classes}
      columns={TABLE_COLUMNS}
      data={deals}
      isDragDisabled
      isResizeDisabled
      totalPages={totalPages}
      isDataLoading={isLoading}
      customSizes={CUSTOM_COL_SIZES}
      sortingValues={SORTING_VALUES}
      headingTranslations={HEADING_TRANSLATIONS}
      tableContentTransformations={renderedContent}
      onRowClick={({ _id }) => navigate(getSellingPortfolio(_id))}
    />
  );
};

export default AnalyticsDeals;
